import { useCallback, useEffect } from 'react';
import type { AxiosError, AxiosResponse } from 'axios';
import { isApiErrorResponse, isWithStatus } from '../common/ApiClient';
import { useInterval } from '../common/useInterval';
import {
  type HLockDto,
  useAcquireHLockMutation,
} from './query/useAcquireHLockMutation';
import { useReleaseHLockMutation } from './query/useReleaseHLockMutation';

const LOCK_EXTENSION_INTERVAL = 110_000;

export const useHLock = (path: string[]) => {
  const hLock = useAcquireOrExtendHLock(path);

  useReleaseHLockOnUnmount(path, hLock);

  return hLock;
};

const useAcquireOrExtendHLock = (path: string[]) => {
  const { mutate: acquire, error } = useAcquireHLockMutation();

  const acquireOrExtend = useCallback(() => acquire({ path }), [acquire, path]);

  useInterval(acquireOrExtend, LOCK_EXTENSION_INTERVAL, true);

  return isLocked(error) ? error.response.data : null;
};

const useReleaseHLockOnUnmount = (
  path: string[],
  preExistingHLock: HLockDto | null,
) => {
  const { mutate: release } = useReleaseHLockMutation();

  useEffect(
    () => {
      // if there was already a lock in existence before we tried to acquire one,
      // then it belongs to someone else and is not for us to release.
      if (preExistingHLock) {
        return;
      }
      return () => release({ path });
    },
    [preExistingHLock], // eslint-disable-line react-hooks/exhaustive-deps
  );
};

type AcquisitionError = AxiosError & {
  status: 423;
  response: AxiosResponse<HLockDto>;
};

const isLocked = (error?: any): error is AcquisitionError =>
  isApiErrorResponse(error) && isWithStatus(error, 423);
